import React, { useEffect, useRef, useState } from "react"
import { Form, Formik, FormikHelpers } from "formik"
import Split from "react-split-it"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import useSwal from "@common-hooks/useSwal"
import moment from "moment"
import clsx from "clsx"

// UI Components
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyModal from "@common-utils/MyModal"
import MyAlert from "@common-utils/MyAlert"
import GlobalHelper from "@common-helpers/GlobalHelper"
import MyRadiosWithLabelAndDescriptionFormField from "@common-components/fields/MyRadiosWithLabelAndDescriptionFormField"

// Models & Logic
import {
     CONST_CANDIDAT_TYPE_CS,
     CONST_CANDIDAT_TYPE_POSTULANT,
     CONST_CANDIDAT_TYPE_SAISI,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_PERSONNEL_TEMPLATE_CATEGORIE_CODE_CANDIDAT_REUNION_QUALIF,
} from "@common-constants/*"
import { CandidatureModel, IHttpErrorResponseModel, IPersonnelTemplateModel } from "@common-models/*"
import { EntretienQualifValidationSchema, IFormValuesModel, initialValues } from "./components/LogicQualif"

// Sub-components
import TemplateSelectorQualif from "./components/TemplateSelectorQualif"
import EntretienQualifFormFields from "./components/FieldsQualif"
import DocosList from "../agenceVisio/components/DocosList"

import { IAddEntretienClientRequestModel, ICandidatAddSuiviEntretienClientFormModel } from "../core/_models"
import { addSuiviEntretienClient } from "../core/_requests"
import { deletePersonnelTemplate, getDetailsPersonnelTemplate } from "../../../../personnel/mon-compte/templates/core/_request"
import PersonnelTemplateCreateForm from "../../../../personnel/mon-compte/templates/_PersonnelTemplateCreateForm"
import PersonnelTemplateEditForm from "../../../../personnel/mon-compte/templates/_PersonnelTemplateEditForm"
import { getCandidatures } from "../../core/_requests"

const CvViewer: React.FC<{ url: string }> = ({ url }) => {
     const [isLoading, setIsLoading] = useState(true)
     return (
          <div className="position-relative w-100 h-100 bg-gray-100 rounded border border-gray-300 overflow-hidden">
               {isLoading && (
                    <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white top-0 start-0 z-index-1">
                         <MySimpleSpinner size="lg" />
                    </div>
               )}
               <iframe allowFullScreen={true} src={url} style={{ height: "100%", width: "100%", border: "none" }} title="CV Viewer" onLoad={() => setIsLoading(false)} />
          </div>
     )
}

const CandidatAddSuiviEntretienQualifForm: React.FC<ICandidatAddSuiviEntretienClientFormModel> = ({ candidat, curriculumGoogleDriveUrl, handleSubmitCallBack = () => {} }) => {
     const { swal } = useSwal()
     const formRef = useRef<any>(null)

     const [isXlScreen, setIsXlScreen] = useState<boolean>(window.innerWidth >= 1200)
     const [selectedTabMobile, setSelectedTabMobile] = useState<"cv" | "doco" | "form">("form")
     const [selectedTabXl, setSelectedTabXl] = useState<"cv" | "doco">("cv")

     const [isFetchingTemplate, setIsFetchingTemplate] = useState<boolean>(false)

     // État Contexte Candidat
     const [candidatures, setCandidatures] = useState<CandidatureModel[]>([])
     const [isLoadingCandidatures, setIsLoadingCandidatures] = useState(false)

     const [isAddingNewTemplate, setIsAddingNewTemplate] = useState<boolean>(false)
     const [templateInEdition, setTemplateInEdition] = useState<{ id: number; title?: string } | null>(null)
     const [editorContentRefresh, setEditorContentRefresh] = useState<{ value: string; random: string } | undefined>(undefined)
     const [templatesRefreshKey, setTemplatesRefreshKey] = useState<number>(0)

     // Récupération des candidatures
     useEffect(() => {
          if (candidat.type === CONST_CANDIDAT_TYPE_POSTULANT && candidat.id) {
               setIsLoadingCandidatures(true)
               getCandidatures(candidat.id)
                    .then(r => setCandidatures(r.data))
                    .catch(() => toast.error("Impossible de récupérer les offres du candidat"))
                    .finally(() => setIsLoadingCandidatures(false))
          }
     }, [candidat.id, candidat.type])

     useEffect(() => {
          const handleResize = () => setIsXlScreen(window.innerWidth >= 1200)
          window.addEventListener("resize", handleResize)
          return () => window.removeEventListener("resize", handleResize)
     }, [])

     const handleFormSubmit = (values: IFormValuesModel, helpers: FormikHelpers<IFormValuesModel>) => {
          helpers.setStatus(null)
          const requestModel: IAddEntretienClientRequestModel = {
               notes: values.notes,
               document: values.document,
               client: values.client as number,
               annonce: values.annonce, // Transfert de l'offre
          }

          addSuiviEntretienClient(candidat.id, requestModel)
               .then(() => {
                    toast.success("Le suivi a bien été ajouté.")
                    handleSubmitCallBack()
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         helpers.setErrors(error.errors)
                    }
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail || "Erreur lors de l'ajout", { autoClose: false })
                    helpers.setSubmitting(false)
               })
     }

     const loadTemplateContent = (templateId: number) => {
          setIsFetchingTemplate(true)
          getDetailsPersonnelTemplate(templateId)
               .then(r => setEditorContentRefresh({ value: r.data.contenu as string, random: GlobalHelper.randomId() }))
               .catch((e: AxiosError) => toast.error(e?.response?.data.detail || "Erreur de chargement du template"))
               .finally(() => setIsFetchingTemplate(false))
     }

     const handleDeleteTemplate = (id: number) => {
          swal.fire({
               icon: "warning",
               title: "Suppression de template",
               text: "Voulez-vous vraiment supprimer ce template ?",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: () => {
                    return deletePersonnelTemplate(id)
                         .then(() => {
                              toast.info("Le template a bien été supprimé")
                              formRef.current?.setFieldValue("template", undefined)
                              setTemplatesRefreshKey(prev => prev + 1)
                         })
                         .catch(e => toast.error(e?.response?.data?.detail))
               },
          })
     }

     const handleTemplateEditSuccess = (editedTemplate: IPersonnelTemplateModel) => {
          setTemplateInEdition(null)
          setTemplatesRefreshKey(prev => prev + 1)
          if (formRef.current?.values?.template === editedTemplate.id) {
               setEditorContentRefresh({ value: editedTemplate.contenu as string, random: GlobalHelper.randomId() })
          }
     }

     // Initialisation des valeurs avec la logique de sélection automatique
     const computedInitialValues: IFormValuesModel = {
          ...initialValues,
          annonce: candidatures.length === 1 ? (candidatures[0].annonce?.id as number) : undefined,
          candidatType: candidat.type || "",
     }

     const radioOptions = [
          {
               label: "Suivi avec pré-saisie",
               description: "Ajouter mes notes à l'aide du template de mon choix (recommandé)",
               faIcon: "file-lines",
               radioValue: "AVEC_TEMPLATE",
               iconColor: "primary" as const,
               iconWrapperColor: "light-primary" as const,
          },
          {
               label: "Suivi vierge",
               description: "Ajouter mes notes sans template",
               faIcon: "file",
               radioValue: "SANS_TEMPLATE",
               iconColor: "success" as const,
               iconWrapperColor: "light-success" as const,
          },
     ]

     const formContent = (
          <Formik<IFormValuesModel>
               initialValues={computedInitialValues}
               onSubmit={handleFormSubmit}
               validationSchema={EntretienQualifValidationSchema}
               innerRef={formRef}
               enableReinitialize={true}
          >
               {helpers => (
                    <Form noValidate autoComplete="off" className="p-1 h-100">
                         {helpers.status && (
                              <MyAlert type={"danger"} classNames={"mb-4"}>
                                   {helpers.status}
                              </MyAlert>
                         )}

                         {/* --- SECTION CONTEXTE DU CANDIDAT --- */}
                         <div className="rounded border border-dashed border-gray-300 p-4 bg-light-secondary bg-opacity-20 mb-5">
                              <label className="fs-7 fw-boldest text-uppercase mb-3 text-gray-600 d-block">Contexte du candidat</label>

                              {candidat.type === CONST_CANDIDAT_TYPE_POSTULANT && (
                                   <div className="d-flex flex-column gap-2">
                                        <span className="fs-8 fw-bold text-gray-500">
                                             Le candidat a postulé à {candidatures.length} {candidatures.length > 1 ? "offres" : "offre"}.
                                        </span>
                                        {isLoadingCandidatures ? (
                                             <div className="py-2">
                                                  <MySimpleSpinner size="sm" />
                                             </div>
                                        ) : (
                                             <select
                                                  className={clsx(
                                                       "form-select form-select-sm form-select-solid",
                                                       helpers.errors.annonce && helpers.touched.annonce && "is-invalid"
                                                  )}
                                                  value={helpers.values.annonce}
                                                  onChange={e => helpers.setFieldValue("annonce", e.target.value)}
                                             >
                                                  <option value="">Sélectionner l'offre concernée...</option>
                                                  {candidatures.map(c => (
                                                       <option key={c.annonce?.id} value={c.annonce?.id}>
                                                            {c.annonce?.intitule || "Sans titre"} — {moment(c.date).format("DD/MM/YYYY")}
                                                       </option>
                                                  ))}
                                             </select>
                                        )}
                                        {helpers.errors.annonce && helpers.touched.annonce && <div className="text-danger fs-8 mt-1">{helpers.errors.annonce as string}</div>}
                                   </div>
                              )}

                              {candidat.type === CONST_CANDIDAT_TYPE_CS && (
                                   <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-3">
                                        <i className="fas fa-info-circle text-primary fs-4 me-3"></i>
                                        <div className="fs-7 fw-bold text-primary">Le candidat a postulé en candidature spontanée.</div>
                                   </div>
                              )}

                              {candidat.type === CONST_CANDIDAT_TYPE_SAISI && (
                                   <div className="notice d-flex bg-light-info rounded border-info border border-dashed p-3">
                                        <i className="fas fa-user-edit text-info fs-4 me-3"></i>
                                        <div className="fs-7 fw-bold text-info">Le candidat a été ajouté manuellement par le staff et n'est lié à aucune offre.</div>
                                   </div>
                              )}
                         </div>

                         <MyRadiosWithLabelAndDescriptionFormField
                              name="createSuiviSansTemplateOuAvecTemplate"
                              formikProps={helpers}
                              radios={radioOptions}
                              classNameLayout="mb-5"
                         />

                         {helpers.values.createSuiviSansTemplateOuAvecTemplate && (
                              <div className="card shadow-sm animate__animated animate__fadeIn">
                                   <div className="card-body">
                                        {helpers.values.createSuiviSansTemplateOuAvecTemplate === "AVEC_TEMPLATE" && (
                                             <TemplateSelectorQualif
                                                  onTemplateSelect={loadTemplateContent}
                                                  onCreateTemplate={() => setIsAddingNewTemplate(true)}
                                                  onEditTemplate={id => setTemplateInEdition({ id })}
                                                  onDeleteTemplate={handleDeleteTemplate}
                                                  refreshKey={templatesRefreshKey}
                                             />
                                        )}

                                        {(helpers.values.createSuiviSansTemplateOuAvecTemplate === "SANS_TEMPLATE" || helpers.values.template) && (
                                             <>
                                                  <div className="separator mb-5"></div>
                                                  <EntretienQualifFormFields isFetchingContent={isFetchingTemplate} forceEditorToRefreshContent={editorContentRefresh} />
                                             </>
                                        )}
                                   </div>

                                   <div className="card-footer d-flex justify-content-end py-4">
                                        <button
                                             className="btn btn-primary fw-boldest"
                                             type="submit"
                                             disabled={
                                                  helpers.isSubmitting || (helpers.values.createSuiviSansTemplateOuAvecTemplate === "AVEC_TEMPLATE" && !helpers.values.template)
                                             }
                                        >
                                             {!helpers.isSubmitting ? "Enregistrer" : <MySimpleSpinner size="sm" />}
                                        </button>
                                   </div>
                              </div>
                         )}
                    </Form>
               )}
          </Formik>
     )

     return (
          <>
               {isXlScreen ? (
                    <Split sizes={[50, 50]} direction="horizontal" gutterSize={10}>
                         <div className="pe-3 overflow-auto" style={{ maxHeight: "82vh" }}>
                              {formContent}
                         </div>
                         <div className="ps-2 d-flex flex-column" style={{ height: "82vh" }}>
                              <div className="d-flex justify-content-end mb-3 flex-shrink-0">
                                   <div className="btn-group">
                                        <button
                                             type="button"
                                             className={`btn btn-sm ${selectedTabXl === "cv" ? "btn-dark" : "btn-outline btn-outline-dark"}`}
                                             onClick={() => setSelectedTabXl("cv")}
                                        >
                                             CV
                                        </button>
                                        <button
                                             type="button"
                                             className={`btn btn-sm ${selectedTabXl === "doco" ? "btn-dark" : "btn-outline btn-outline-dark"}`}
                                             onClick={() => setSelectedTabXl("doco")}
                                        >
                                             DoCo
                                        </button>
                                   </div>
                              </div>
                              <div className="bg-white rounded flex-grow-1 overflow-hidden position-relative h-100 border border-gray-200 shadow-sm">
                                   {selectedTabXl === "cv" ? <CvViewer url={curriculumGoogleDriveUrl} /> : <DocosList candidatId={candidat.id} />}
                              </div>
                         </div>
                    </Split>
               ) : (
                    <>
                         <div className="d-flex justify-content-center mb-4 sticky-top bg-white py-2 z-index-2">
                              <div className="btn-group shadow-sm">
                                   <button className={`btn btn-sm ${selectedTabMobile === "form" ? "btn-primary" : "btn-light"}`} onClick={() => setSelectedTabMobile("form")}>
                                        <i className="fas fa-edit me-2"></i>Form
                                   </button>
                                   <button className={`btn btn-sm ${selectedTabMobile === "cv" ? "btn-primary" : "btn-light"}`} onClick={() => setSelectedTabMobile("cv")}>
                                        <i className="fas fa-file-pdf me-2"></i>CV
                                   </button>
                                   <button className={`btn btn-sm ${selectedTabMobile === "doco" ? "btn-primary" : "btn-light"}`} onClick={() => setSelectedTabMobile("doco")}>
                                        <i className="fas fa-folder-open me-2"></i>Docs
                                   </button>
                              </div>
                         </div>
                         <div className={selectedTabMobile === "form" ? "d-block" : "d-none"}>{formContent}</div>
                         {selectedTabMobile === "cv" && (
                              <div style={{ height: "82vh" }}>
                                   <CvViewer url={curriculumGoogleDriveUrl} />
                              </div>
                         )}
                         {selectedTabMobile === "doco" && (
                              <div style={{ height: "82vh" }}>
                                   <DocosList candidatId={candidat.id} />
                              </div>
                         )}
                    </>
               )}

               {isAddingNewTemplate && (
                    <MyModal title="Création d'un template 'RQ'" show={true} onCloseCallback={() => setIsAddingNewTemplate(false)} fullscreen>
                         <PersonnelTemplateCreateForm
                              categorie={CONST_PERSONNEL_TEMPLATE_CATEGORIE_CODE_CANDIDAT_REUNION_QUALIF}
                              handleOnSubmitCallBack={() => {
                                   setIsAddingNewTemplate(false)
                                   setTemplatesRefreshKey(prev => prev + 1)
                              }}
                         />
                    </MyModal>
               )}

               {templateInEdition && (
                    <MyModal title="Édition du Template" show={true} onCloseCallback={() => setTemplateInEdition(null)} fullscreen>
                         <PersonnelTemplateEditForm template_ID={templateInEdition.id} handleOnSubmitCallBack={handleTemplateEditSuccess} />
                    </MyModal>
               )}
          </>
     )
}

export default CandidatAddSuiviEntretienQualifForm
